import { useMemo } from "react";

import { breadcrumbConfig } from "@/constants/finder/config";
import { CountryCode } from "@/custom-types/CountryCode";
import { Location } from "@/custom-types/Location";
import { FinderBreadcrumbLocation, RetailType } from "@/custom-types/Store";
import { getLocationRoute } from "@/utils/locationUtils";

import Breadcrumb from "@/components/Breadcrumb";
import Breadcrumbs from "@/components/Breadcrumbs";

const StoreBreadcrumbs = ({
  isLocationPage,
  pageLocation,
  countryCode,
  retailType,
}: {
  isLocationPage: boolean;
  pageLocation: Location;
  countryCode: CountryCode;
  retailType: RetailType;
}) => {
  const { finderRoute, title } = useMemo(() => {
    switch (countryCode) {
      case "US": {
        return breadcrumbConfig[countryCode][retailType];
      }
      case "CA": {
        return breadcrumbConfig[countryCode][retailType];
      }
      default: {
        return breadcrumbConfig["US"][retailType];
      }
    }
  }, [countryCode, retailType]);

  const generateUrl = (type: string): string => {
    switch (type) {
      case "home":
        return "/";
      case "siteMap":
        return "/dispensaries/locations";
      case "activeView":
        return finderRoute;
      case "state":
        return `${getLocationRoute(finderRoute, {
          state: pageLocation.state,
        })}`;
      case "city":
        return `${getLocationRoute(finderRoute, {
          city: pageLocation.city,
          slug: pageLocation.geoSlug,
          state: pageLocation.state,
        })}`;
      case "zip":
        return `${getLocationRoute(
          finderRoute,
          pageLocation as FinderBreadcrumbLocation,
        )}`;
      default:
        return "";
    }
  };

  const arrayOfLinks: { label: string; url?: string }[] = [
    { label: title, url: generateUrl("activeView") },
    {
      label: pageLocation.countryCode === "CA" ? "Canada" : "United States",
      url: generateUrl("siteMap"),
    },
  ];

  if (pageLocation.state) {
    arrayOfLinks.push({
      label: pageLocation.state,
      url: generateUrl("state"),
    });
  }
  if (pageLocation.city) {
    arrayOfLinks.push({
      label: pageLocation.city,
      url: generateUrl("city"),
    });
  }

  if (pageLocation.isUserLocation) {
    arrayOfLinks.push({ label: "My location" });
  } else if (pageLocation.zip || pageLocation?.street?.name) {
    const streetNumber = pageLocation?.street?.number || "";
    const streetName = pageLocation?.street?.name || "";
    const zip = pageLocation.zip || "";
    const shouldHaveComma =
      pageLocation?.street?.number &&
      pageLocation?.street?.name &&
      pageLocation.zip
        ? ", "
        : "";
    arrayOfLinks.push({
      label: `${streetNumber} ${streetName}${shouldHaveComma}${zip}`,
    });
  }

  return (
    <div>
      <Breadcrumbs orderedListClassNames="!m-0" includeSchema={isLocationPage}>
        {arrayOfLinks.map(({ url, label }, idx) => (
          <Breadcrumb
            key={label}
            href={url}
            currentLocation={idx === arrayOfLinks.length - 1}
          >
            {label}
          </Breadcrumb>
        ))}
      </Breadcrumbs>
    </div>
  );
};

export default StoreBreadcrumbs;
